import { ChangeDetectorRef, Component, EventEmitter, OnDestroy } from '@angular/core';
import { IModalComponent } from '@lib/modal';
import { BehaviorSubject, Subject } from 'rxjs';
import { TranslateService } from '@project/translate';
import { CsvParserService } from '@core/services';
import { FormBuilder } from '@angular/forms';
import { NotificationsService } from '@lib/notifications';
import { map, takeUntil } from 'rxjs/operators';
import { EGenderDTO } from '@project/view-models';
import { FormHelpers, IRowValidationResult } from '@core/helpers';
import { IReceptionistInviteFormModel, ReceptionistInviteUtils } from '../receptionist-invite.common';

@Component({
  selector: 'app-receptionist-invite-import-modal',
  templateUrl: './receptionist-invite-import-modal.component.html',
  styleUrls: ['./receptionist-invite-import-modal.component.scss'],
})
export class ReceptionistInviteImportModalComponent implements IModalComponent, OnDestroy {
  close$ = new EventEmitter<void>();
  submit$ = new EventEmitter();

  private _validItems$ = new BehaviorSubject<IRowValidationResult<IReceptionistInviteFormModel>[]>([]);
  public validItems$ = this._validItems$.asObservable();

  private _invalidItems$ = new BehaviorSubject<IRowValidationResult<IReceptionistInviteFormModel>[]>([]);
  public invalidItems$ = this._invalidItems$.asObservable();

  public readonly columnLabelsMap: { [K in keyof IReceptionistInviteFormModel]: string } = {
    firstName: TranslateService.localize('nouns.first-name'),
    lastName: TranslateService.localize('nouns.last-name'),
    email: TranslateService.localize('nouns.email'),
    gender: TranslateService.localize('nouns.gender'),
    phone: TranslateService.localize('nouns.phone'),
    dateOfBirth: TranslateService.localize('nouns.date-of-birth'),
  };

  private readonly destroy$ = new Subject();

  constructor(
    private csvParserService: CsvParserService,
    private formBuilder: FormBuilder,
    private notificationsService: NotificationsService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  close() {
    this.close$.emit();
  }

  doSuccess() {
    this.submit$.next(
      this._validItems$.value.map((record) => ReceptionistInviteUtils.createReceptionistInviteDto(record.record)),
    );
  }

  onSelectFile(event: Event) {
    this._invalidItems$.next([]);
    this._validItems$.next([]);

    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files[0];
    if (!file) {
      return;
    }

    this.csvParserService
      .parseFile(file, ['firstName', 'lastName', 'email', 'gender', 'phone', 'dateOfBirth'])
      .pipe(
        map((records) =>
          records.map(
            (record): IReceptionistInviteFormModel => ({
              ...record,
              firstName: record.firstName.trim(),
              lastName: record.lastName.trim(),
              phone: record.phone.trim(),
              email: record.email.trim(),
              dateOfBirth: record.dateOfBirth.trim().replace(/[/]/g, '.'),
              gender: EGenderDTO[record.gender.trim()] ?? record.gender.trim(),
            }),
          ),
        ),
        map((records) =>
          FormHelpers.validateRecords(
            records,
            this.formBuilder.group(ReceptionistInviteUtils.getReceptionistInviteFormDeclaration()),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(
        (validatedResult: IRowValidationResult<IReceptionistInviteFormModel>[]) => {
          let invalid: IRowValidationResult<IReceptionistInviteFormModel>[] = [];
          let valid: IRowValidationResult<IReceptionistInviteFormModel>[] = [];

          invalid = validatedResult.filter((validatedRecord) => !validatedRecord.isValid);
          valid = validatedResult
            .filter((validatedRecord) => validatedRecord.isValid)
            .map((validatedRecord) => validatedRecord);

          const res = [];
          validatedResult.map((item: IRowValidationResult<IReceptionistInviteFormModel>) => {
            const existItem = res.find((x) => x.record.email === item.record.email);
            if (existItem) {
              invalid.push(item);
            }
            res.push(item);
          }, []);

          invalid = invalid.filter((v, i, a) => a.findIndex((t) => t.record.email === v.record.email) === i);

          valid = valid.filter((v) => invalid.find((r) => r) !== v);

          this._validItems$.next(valid);
          this._invalidItems$.next(invalid);
        },
        (err) => {
          console.warn(err);
          this.notificationsService.error({
            message: TranslateService.localize('import-invites.errors.parse-file-error'),
          });

          fileInput.value = null;
          this.changeDetectorRef.markForCheck();
        },
      );
  }

  ngOnDestroy() {
    this.destroy$.next();
  }
}
